import { useState, Fragment } from 'react';
import { useToast } from '../../context/ToastContext.jsx';
import { useApi } from '../../hooks/useApi.js';
import { usePagedList } from '../../hooks/usePagedList.js';
import SortableTh from '../../components/ui/SortableTh.jsx';
import Pagination from '../../components/ui/Pagination.jsx';
import { fmtTime } from '../../utils/format.js';
import { adminGetInquiries, adminGetInquiryProductOptions,
  adminUpdateInquiryStatus, adminUpdateInquiryAmount, adminDeleteInquiry } from '../../api/inquiriesApi.js';

const STATUS_OPTIONS = [
  { value: 'SUBMITTED', label: '已送出' },
  { value: 'PROCESSING', label: '處理中' },
  { value: 'PAID', label: '已收錢（未結案）' },
  { value: 'DONE', label: '已完成' },
  { value: 'CLOSED', label: '已結案' },
  { value: 'CANCELLED', label: '已取消' },
];

const PAY_LABEL = { transfer: '銀行轉帳', linepay: 'LINE Pay', paypal: 'PayPal' };

function AdminInquiriesPage() {
  const { showToast } = useToast();
  const [expanded, setExpanded] = useState(null); // 展開明細的登記單 id
  const [savingId, setSavingId] = useState(null);
  const [amountDraft, setAmountDraft] = useState({}); // 改金額草稿：{ itemId: amount }
  const [savingAmount, setSavingAmount] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const { data: productOptions = [], refetch: refetchProductOptions } = useApi(
    adminGetInquiryProductOptions, []);
  const list = usePagedList(p => adminGetInquiries(p), {
    limit: 10, initialFilters: { status: '', productId: '' } });
  const { items, total, totalPages, page, setPage, sort, toggleSort, filters, setFilter, q, setQ, loading } = list;

  const changeStatus = async (inq, next) => {
    if (next === inq.status) return;
    setSavingId(inq.id);
    try {
      await adminUpdateInquiryStatus(inq.id, next);
      showToast(`${inq.refNo} 已改為「${STATUS_OPTIONS.find(s => s.value === next)?.label}」`, 'success');
      await Promise.all([list.refetch(), refetchProductOptions()]);
    } catch (err) {
      showToast(`更新失敗：${err.message}`, 'error');
    } finally {
      setSavingId(null);
    }
  };

  // 展開明細時，把各列金額載入草稿（供編輯）
  const toggleExpand = (inq) => {
    if (expanded === inq.id) { setExpanded(null); return; }
    setExpanded(inq.id);
    const draft = {};
    (inq.items || []).forEach(it => { draft[it.id] = it.amount; });
    setAmountDraft(draft);
  };

  // 儲存改後的金額：逐列送出，總額由後端重算（不得低於已折抵）
  const saveAmounts = async (inq) => {
    setSavingAmount(true);
    try {
      const items = (inq.items || []).map(it => ({ id: it.id, amount: Number(amountDraft[it.id]) || 0 }));
      await adminUpdateInquiryAmount(inq.id, items);
      showToast(`${inq.refNo} 金額已更新`, 'success');
      list.refetch();
      setExpanded(null);
    } catch (err) {
      showToast(`更新失敗：${err.message}`, 'error');
    } finally {
      setSavingAmount(false);
    }
  };

  const deleteInquiry = async (inq) => {
    const confirmed = window.confirm(
      `\u78ba\u5b9a\u8981\u6c38\u4e45\u522a\u9664\u767b\u8a18 ${inq.refNo || inq.id} \u55ce\uff1f\n\n\u9019\u500b\u52d5\u4f5c\u7121\u6cd5\u5fa9\u539f\uff0c\u5df2\u6298\u62b5\u7684\u9ede\u6578\u6703\u81ea\u52d5\u9000\u56de\u3002`);
    if (!confirmed) return;
    setDeletingId(inq.id);
    try {
      await adminDeleteInquiry(inq.id);
      if (expanded === inq.id) setExpanded(null);
      showToast(`\u5df2\u6c38\u4e45\u522a\u9664 ${inq.refNo || inq.id}`, 'success');
      await Promise.all([list.refetch(), refetchProductOptions()]);
    } catch (err) {
      showToast(`\u522a\u9664\u5931\u6557\uff1a${err.message}`, 'error');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <div className="admin-page-head">
        <h1>登記管理<small>INQUIRIES</small></h1>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <input
          className="au-search"
          placeholder="搜尋編號 / 商品…"
          value={q}
          onChange={e => setQ(e.target.value)}
          style={{ minWidth: 220, padding: '10px 14px', border: '1.5px solid var(--line)', borderRadius: 8, font: '600 14px Inter', outline: 'none' }}
        />
        <select
          className="admin-select"
          style={{ maxWidth: 280 }}
          value={filters.productId}
          onChange={e => setFilter('productId', e.target.value)}
        >
          <option value="">{'\u5168\u90e8\u5546\u54c1'}</option>
          {productOptions.map(product => (
            <option key={product.productId} value={product.productId}>
              {product.productName} ({product.incompleteCount} {'\u7b46\u672a\u5b8c\u6210'})
            </option>
          ))}
        </select>
        <select
          className="admin-select"
          style={{ maxWidth: 180 }}
          value={filters.status}
          onChange={e => setFilter('status', e.target.value)}
        >
          <option value="">全部狀態</option>
          {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <span style={{ marginLeft: 'auto', color: 'var(--ink-soft)', fontSize: 13 }}>共 {total} 筆</span>
      </div>

      <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="admin-table">
          <thead>
            <tr>
              <SortableTh field="refNo" sort={sort} onSort={toggleSort} style={{ width: 130 }}>編號</SortableTh>
              <th style={{ width: 140 }}>登記人</th>
              <SortableTh field="productName" sort={sort} onSort={toggleSort}>商品</SortableTh>
              <th style={{ width: 70, textAlign: 'center' }}>筆數</th>
              <SortableTh field="payMethod" sort={sort} onSort={toggleSort} style={{ width: 100 }}>付款</SortableTh>
              <SortableTh field="estimatedTotal" sort={sort} onSort={toggleSort} style={{ width: 110, textAlign: 'right' }}>預估總額</SortableTh>
              <SortableTh field="createdAt" sort={sort} onSort={toggleSort} style={{ width: 150 }}>時間</SortableTh>
              <SortableTh field="status" sort={sort} onSort={toggleSort} style={{ width: 130 }}>狀態</SortableTh>
              <th style={{ width: 80 }}>明細</th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0
              ? <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-soft)' }}>載入中…</td></tr>
              : items.length === 0
                ? <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-soft)' }}>尚無登記</td></tr>
                : items.map(inq => (
                  <Fragment key={inq.id}>
                    <tr>
                      <td style={{ font: '700 13px monospace', color: 'var(--navy)' }}>{inq.refNo || '—'}</td>
                      <td>
                        <div style={{ font: '700 13px "Noto Sans TC"', color: 'var(--navy)' }}>{inq.userName || '—'}</div>
                        {inq.userEmail && <div style={{ font: '500 11px Inter', color: 'var(--ink-soft)' }}>{inq.userEmail}</div>}
                      </td>
                      <td style={{ font: '700 14px "Noto Sans TC"', color: 'var(--navy)' }}>{inq.productName}</td>
                      <td style={{ textAlign: 'center' }}>{inq.items?.length ?? 0}</td>
                      <td style={{ fontSize: 13 }}>{PAY_LABEL[inq.payMethod] || inq.payMethod}</td>
                      <td style={{ textAlign: 'right' }}>
                        {inq.pointsRedeemed > 0 ? (
                          <>
                            <div style={{ font: '600 11px Inter', color: 'var(--ink-soft)', textDecoration: 'line-through' }}>NT${(inq.estimatedTotal ?? 0).toLocaleString()}</div>
                            <div style={{ font: '800 14px Inter', color: 'var(--navy)' }}>NT${Math.max(0, (inq.estimatedTotal ?? 0) - inq.pointsRedeemed).toLocaleString()}</div>
                            <div style={{ font: '600 10px Inter', color: 'var(--red)' }}>折 {inq.pointsRedeemed.toLocaleString()}P</div>
                          </>
                        ) : (
                          <span style={{ font: '700 14px Inter', color: 'var(--navy)' }}>NT${(inq.estimatedTotal ?? 0).toLocaleString()}</span>
                        )}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--ink-soft)' }}>{fmtTime(inq.createdAt) || '—'}</td>
                      <td>
                        <select
                          className="admin-select"
                          value={inq.status}
                          disabled={savingId === inq.id}
                          onChange={e => changeStatus(inq, e.target.value)}
                        >
                          {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </td>
                      <td>
                        <button className="admin-btn ghost sm" onClick={() => toggleExpand(inq)}>
                          {expanded === inq.id ? '收合' : '展開'}
                        </button>
                        <button className="admin-btn danger sm" disabled={deletingId === inq.id}
                          onClick={() => deleteInquiry(inq)}>
                          {deletingId === inq.id ? '\u522a\u9664\u4e2d\u2026' : '\u522a\u9664'}
                        </button>
                      </td>
                    </tr>
                    {expanded === inq.id && (
                      <tr>
                        <td colSpan={9} style={{ background: 'var(--ice, #f4f7ff)', padding: 0 }}>
                          <table className="admin-table" style={{ margin: 0 }}>
                            <thead>
                              <tr>
                                <th style={{ width: 80 }}>#</th>
                                <th>種類 / 角色</th>
                                <th>卡名</th>
                                <th>帳號（信箱）</th>
                                <th>備註</th>
                                <th style={{ textAlign: 'right', width: 90 }}>金額</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(inq.items || []).map((it, i) => (
                                <tr key={it.id ?? i}>
                                  <td>{i + 1}</td>
                                  <td>{it.variantName || '—'}</td>
                                  <td>{it.cardName || '—'}</td>
                                  <td>{it.account || '—'}</td>
                                  <td>{it.note || '—'}</td>
                                  <td style={{ textAlign: 'right' }}>
                                    <input type="number" min="0" step="1" value={amountDraft[it.id] ?? it.amount}
                                      onChange={e => setAmountDraft(d => ({ ...d, [it.id]: e.target.value }))}
                                      style={{ width: 90, textAlign: 'right', padding: '5px 8px', border: '1.5px solid var(--line)', borderRadius: 6, font: '600 13px Inter' }} />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', flexWrap: 'wrap' }}>
                            {inq.pointsRedeemed > 0 && (
                              <>
                                <span style={{ font: '600 13px "Noto Sans TC", sans-serif', color: 'var(--ink-soft)' }}>預估總額 NT${(inq.estimatedTotal ?? 0).toLocaleString()}</span>
                                <span style={{ font: '700 13px "Noto Sans TC", sans-serif', color: 'var(--red)' }}>已折抵 {inq.pointsRedeemed.toLocaleString()} P</span>
                                <span style={{ font: '800 14px "Noto Sans TC", sans-serif', color: 'var(--navy)' }}>應付 NT${Math.max(0, (inq.estimatedTotal ?? 0) - inq.pointsRedeemed).toLocaleString()}</span>
                              </>
                            )}
                            <span style={{ color: 'var(--ink-soft)', fontSize: 12 }}>改完金額按右側儲存（總額自動重算，不得低於已折抵）</span>
                            <button className="admin-btn sm" style={{ marginLeft: 'auto' }} disabled={savingAmount}
                              onClick={() => saveAmounts(inq)}>
                              {savingAmount ? '儲存中…' : '儲存金額'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))
            }
          </tbody>
        </table>
      </div>
      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
    </>
  );
}

export default AdminInquiriesPage;
